import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Download, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import PowerPointInstructions from './PowerPointInstructions';
import { withBackend } from '../utils/api';
import { toast } from 'sonner';

export default function RedesignSlideView({
  slide,
  slideNumber,
  totalSlides,
  onPrevious,
  onNext,
  analysis
}) {
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructions, setInstructions] = useState(null);
  const [loadingInstructions, setLoadingInstructions] = useState(false);
  const [exportingSlide, setExportingSlide] = useState(false);

  const handleGenerateInstructions = async () => {
    if (instructions) {
      // Toggle if already loaded
      setShowInstructions(!showInstructions);
      return;
    }

    try {
      setLoadingInstructions(true);

      const response = await fetch(withBackend('/generate-ppt-instructions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slide_number: slideNumber,
          slide_data: slide,
          analysis: analysis || {}
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate instructions');
      }

      const data = await response.json();
      setInstructions(data);
      setShowInstructions(true);

    } catch (error) {
      console.error('Failed to generate instructions:', error);
      alert('Failed to generate PowerPoint instructions. Please try again.');
    } finally {
      setLoadingInstructions(false);
    }
  };

  const handleExportSlide = async () => {
    // For SVG-based export, we don't require nano_banana_preview_url
    // since we generate fresh background and SVG overlay

    // Get the best available title
    const title = slide.recommendations?.newTitle ||
                  slide.title ||
                  (slide.text_content?.match(/SLIDE TITLE:\s*(.+)/i)?.[1]) ||
                  'Untitled Slide';

    // Get bullet points - prefer existing bullets, otherwise extract from text
    let bullets = slide.bullet_points || [];

    // If no bullets, try to extract meaningful content from text_content
    // but filter out metadata like "[GROUPED]", "SLIDE TITLE:", etc.
    if (bullets.length === 0 && slide.text_content) {
      bullets = slide.text_content
        .split('\n')
        .map(line => line.trim())
        .filter(line => {
          if (!line) return false;
          // Filter out metadata lines
          if (line.startsWith('SLIDE TITLE:')) return false;
          if (line.startsWith('[GROUPED]')) return false;
          if (line === 'SECRET') return false;
          if (line === 'Confidential') return false;
          return true;
        });
    }

    const content = '';

    if (!title && bullets.length === 0 && !content) {
      toast.error('No content available to export');
      return;
    }

    try {
      setExportingSlide(true);
      toast.loading('Generating editable PowerPoint (this may take a moment)...', { id: 'export-slide' });

      // Use the new layered export endpoint (SlideSpeak approach)
      // This creates: text-free background + native text boxes = truly editable PPTX
      const response = await fetch(withBackend('/export-layered-pptx'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          presentation_title: `Slide ${slideNumber}`,
          // Pass the redesigned image - the backend will:
          // 1. Extract text regions with bounding boxes
          // 2. Generate a text-free background
          // 3. Place native text boxes over the background
          image_data: slide.nano_banana_preview_url,
          // Optionally pass pre-extracted text regions if available
          text_regions: slide.nano_banana_layout?.text_regions || null
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to export slide');
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `redesigned_slide_${slideNumber}_editable.pptx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Editable slide exported successfully!', { id: 'export-slide' });

    } catch (error) {
      console.error('Failed to export slide:', error);
      toast.error(error.message || 'Failed to export slide', { id: 'export-slide' });
    } finally {
      setExportingSlide(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Slide Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-6 py-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">
            Slide {slideNumber} of {totalSlides}
          </h2>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onPrevious}
              disabled={slideNumber === 1}
              className="text-white hover:bg-purple-700"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onNext}
              disabled={slideNumber === totalSlides}
              className="text-white hover:bg-purple-700"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Slide Content */}
      <div className="p-6">
        {/* Before & After Section */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Original Slide */}
          <div>
            <div className="mb-2">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Original
              </h3>
            </div>
            <div className="bg-slate-100 rounded-lg overflow-hidden border-2 border-slate-200 aspect-video flex items-center justify-center">
              {slide.thumbnail_url ? (
                <img
                  src={slide.thumbnail_url}
                  alt={`Original slide ${slideNumber}`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-slate-400 text-sm">No preview available</div>
              )}
            </div>
          </div>

          {/* Redesigned Slide */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-purple-700 uppercase tracking-wide">
                Redesigned
              </h3>
              {slide.nano_banana_preview_url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportSlide}
                  disabled={exportingSlide}
                  className="text-purple-600 border-purple-300 hover:bg-purple-50"
                >
                  {exportingSlide ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-1" />
                      Export as Slide
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg overflow-hidden border-2 border-purple-300 aspect-video flex items-center justify-center">
              {slide.nano_banana_preview_url ? (
                <img
                  src={slide.nano_banana_preview_url}
                  alt={`Redesigned slide ${slideNumber}`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-purple-400 text-sm">
                  {slide.brief_explanation || 'Preview not available'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Brief Explanation */}
        <div className="bg-slate-50 rounded-lg p-6 mb-4 border border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-3">
            What Changed?
          </h3>
          <p className="text-slate-700 leading-relaxed">
            {slide.brief_explanation || 'This redesign improves the visual hierarchy, typography, and overall professional appearance of the slide.'}
          </p>
        </div>

        {/* How to Create in PPT Button */}
        <div className="border-t border-slate-200 pt-4">
          <Button
            onClick={handleGenerateInstructions}
            disabled={loadingInstructions}
            className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white py-6 text-base font-semibold"
          >
            {loadingInstructions ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                Generating Instructions...
              </>
            ) : showInstructions ? (
              <>
                <ChevronUp className="w-5 h-5 mr-2" />
                Hide PowerPoint Instructions
              </>
            ) : (
              <>
                <ChevronDown className="w-5 h-5 mr-2" />
                How can I create this in PowerPoint?
              </>
            )}
          </Button>

          {/* Instructions Section */}
          {showInstructions && instructions && (
            <div className="mt-6 animate-in slide-in-from-top duration-300">
              <PowerPointInstructions
                instructions={instructions.instructions || []}
                assets={instructions.assets || {}}
                slideNumber={slideNumber}
              />
            </div>
          )}
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex items-center justify-between">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={slideNumber === 1}
          className="flex items-center space-x-2"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Previous Slide</span>
        </Button>

        <div className="text-sm text-slate-600">
          Slide {slideNumber} of {totalSlides}
        </div>

        <Button
          variant="outline"
          onClick={onNext}
          disabled={slideNumber === totalSlides}
          className="flex items-center space-x-2"
        >
          <span>Next Slide</span>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
